import LangLink from '../../../../components/common/LangLink';
import { Check } from 'lucide-react';
import { useTheme } from '../../../../context/ThemeContext';

const LevelsLanding = ({ language }) => {
    const fr = language === 'fr';
    const lang = fr ? 'fr' : 'en';
    const { theme } = useTheme();
    // Video swaps on (lang × theme): EN/FR_DARK.mp4, EN/FR_LIGHT.mp4
    const videoSrc = `/img/landing/${lang.toUpperCase()}_${theme === 'dark' ? 'DARK' : 'LIGHT'}.mp4`;

    // Order matches Figma: Aviation, Helicopter, Planneur, Dispatch
    const features = fr
        ? ['Aviation', 'Hélicoptère', 'Planeur', 'Dispatch']
        : ['Aviation', 'Helicopter', 'Glider', 'Dispatch'];

    return (
        <section className="relative bg-white dark:bg-[#0f0f0f] text-gray-900 dark:text-white">
            <div className="max-w-[1151px] mx-auto px-6 py-20 md:py-28">
                <div className="grid grid-cols-1 md:grid-cols-[521px_505px] gap-12 md:gap-[125px] items-center">
                    {/* LEFT — text + features + CTA */}
                    <div>
                        {/* Kicker */}
                        <p className="font-['Rethink_Sans',sans-serif] font-bold text-[14px] tracking-[3.5px] text-[#71a7ff] leading-[34px]">
                            {fr ? 'LICENCES & QUALIFICATIONS' : 'LICENCES & RATINGS'}
                        </p>

                        {/* Title */}
                        <h2 className="font-['Rethink_Sans',sans-serif] font-normal mt-3 md:mt-4 mb-6 md:mb-8 text-[40px] leading-[40px] tracking-[-1px] md:text-[50px] md:leading-[46px] md:tracking-[-1.5px]">
                            <span className="text-gray-400 dark:text-white/40">{fr ? 'Une préparation pour ' : 'A preparation for '}</span>
                            <span className="text-gray-900 dark:text-white">{fr ? 'chaque étape de ta formation' : 'every step of your training'}</span>
                        </h2>

                        {/* Mobile-only video — between title and body per Figma */}
                        <div className="md:hidden mb-8">
                            <video
                                key={videoSrc}
                                src={videoSrc}
                                autoPlay
                                muted
                                loop
                                playsInline
                                disablePictureInPicture
                                disableRemotePlayback
                                controlsList="nodownload noremoteplayback nofullscreen"
                                className="w-full h-auto"
                                aria-label={fr ? 'Aperçu des niveaux Nizus' : 'Preview of Nizus levels'}
                            />
                        </div>

                        {/* Body */}
                        <p className="font-['Rethink_Sans',sans-serif] font-normal text-[16px] leading-[26px] md:text-[17px] md:leading-[27px] text-gray-600 dark:text-[#a9b1bd] max-w-[460px] mb-10">
                            {fr
                                ? 'Du PSTAR à l’ATPL, des contenus adaptés à chaque niveau pour avancer sereinement jusqu’au jour J.'
                                : 'From PSTAR to ATPL, content tuned to where you are, so you move forward with confidence regardless of the exam.'}
                        </p>

                        {/* Features list — 1 col mobile, 2 cols desktop */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-10 max-w-[400px]">
                            {features.map((feat) => (
                                <div key={feat} className="flex items-center gap-3">
                                    <div className="w-5 h-5 rounded-full bg-[#0061fe] flex items-center justify-center flex-shrink-0">
                                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                                    </div>
                                    <span className="font-['Rethink_Sans',sans-serif] font-medium text-[16px] text-gray-900 dark:text-white">
                                        {feat}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* CTA — centered on mobile, left-aligned on desktop */}
                        <div className="flex justify-center md:justify-start">
                            <LangLink
                                to="/levels"
                                className="inline-flex items-center justify-center bg-[#0061fe] text-white font-['Manrope',sans-serif] font-semibold text-[13px] px-[26px] py-[18px] rounded-full hover:bg-[#0052db] active:scale-[0.98] transition-all shadow-lg shadow-[#0061fe]/20"
                            >
                                {fr ? 'Nos abonnements' : 'Our subscriptions'}
                            </LangLink>
                        </div>
                    </div>

                    {/* RIGHT — video (desktop only, mobile shown above) */}
                    <div className="hidden md:flex items-center justify-center">
                        <video
                            key={videoSrc}
                            src={videoSrc}
                            autoPlay
                            muted
                            loop
                            playsInline
                            className="w-full max-w-[505px] h-auto"
                            aria-label={fr ? 'Aperçu des niveaux Nizus' : 'Preview of Nizus levels'}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LevelsLanding;
